import React, {useMemo, useState} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {IconButton} from "@mui/material";
import {GridColDef} from "@mui/x-data-grid";
import {
  FilterOption,
  countActiveFilters,
  FilterSidebar,
  FiltersToggleButton,
  FilterValues,
  hasActiveFilters,
  LIST_HEADER_HEIGHT,
  ListDataGrid,
  ListFilter,
  ListPageHeader,
  matchesFilters,
  SearchInput,
  SidebarFilterMatchMode,
  sortedByLocale,
  uniqueSorted,
} from "../../ui/components";
import {AudienceRecalculationBanner} from "../../components/AudienceRecalculationBanner";
import {AccountCountInfoTooltip} from "../../components/tooltips";
import {Box, Chip, Colors, Text} from "@bounce/shared/ui";
import {useLocalStorageState} from "@bounce/shared/hooks";
import {prettifyCurrencyRounded} from "@bounce/shared/helpers";
import {useGetCampaigns} from "../../apis/requests/campaigns/getCampaigns";
import {CAMPAIGNS_QUERY_KEY} from "../../apis/requests/queryKeys";
import {CampaignRow} from "../../apis/requests/campaigns/types";
import {toQueryParams} from "../../apis/requests/queryParams";
import {reachUrls} from "../../layout/menu-items";
import {TemplatesPerChannelDrawer} from "../audiences/TemplatesPerChannelDrawer";
import {useDebounce} from "../../hooks/useDebounce";
import {useFilterPanel} from "../../hooks/useFilterPanel";
import {sumTemplatesPerChannel} from "../audiences/templatesPerChannel";

const CAMPAIGNS_FILTERS_STORAGE_KEY = "reach-campaigns-filters";
const CAMPAIGN_SEGMENTS_MATCH_MODE_STORAGE_KEY = "reach-campaigns-segments-match-mode";
const CAMPAIGNS_FILTER_PANEL_STORAGE_KEY = "reach-campaigns-filter-panel-expanded";
const HEADER_CONTROL_HEIGHT = 40;

const DEFAULT_CAMPAIGN_FILTER: FilterOption = {value: "__default__", label: "Default", chipVariant: "inherit"};

const CampaignsPage = () => {
  const {data, isLoading, error} = useGetCampaigns();
  const campaigns = data ?? [];
  const [filterValues, setFilterValues] = useLocalStorageState<FilterValues>(
    CAMPAIGNS_FILTERS_STORAGE_KEY,
    {},
    {encrypted: false}
  );
  const [campaignMatchMode, setCampaignMatchMode] = useLocalStorageState<SidebarFilterMatchMode>(
    CAMPAIGN_SEGMENTS_MATCH_MODE_STORAGE_KEY,
    "any",
    {encrypted: false}
  );
  const filterPanel = useFilterPanel(CAMPAIGNS_FILTER_PANEL_STORAGE_KEY);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [channelsDrawerCampaign, setChannelsDrawerCampaign] = useState<CampaignRow | null>(null);

  const getCampaignUrl = (row: CampaignRow) =>
    `${reachUrls.campaignDetail}${toQueryParams("segment_ids", row.campaign_segment_ids)}`;

  const campaignSegmentOptions = useMemo(
    () => uniqueSorted(campaigns.flatMap((row) => row.campaign_segment_names)),
    [campaigns]
  );

  const filters = useMemo<ListFilter<CampaignRow>[]>(
    () => [
      {
        key: "campaign_segments",
        placeholder: "Campaign segments",
        options: [DEFAULT_CAMPAIGN_FILTER, ...campaignSegmentOptions],
        getRowValues: (row) =>
          row.campaign_segment_names.length === 0 ? [DEFAULT_CAMPAIGN_FILTER.value] : row.campaign_segment_names,
        chipVariant: "inherit",
        matchMode: {value: campaignMatchMode, onChange: setCampaignMatchMode},
      },
    ],
    [campaignSegmentOptions, campaignMatchMode, setCampaignMatchMode]
  );

  const filteredRows = useMemo(() => {
    const searchTerm = debouncedSearch.trim().toLowerCase();
    return campaigns.filter(
      (row) =>
        matchesFilters(row, filters, filterValues) &&
        (searchTerm === "" || row.campaign_segment_names.some((name) => name.toLowerCase().includes(searchTerm)))
    );
  }, [campaigns, filters, filterValues, debouncedSearch]);

  const columns = useMemo<GridColDef<CampaignRow>[]>(
    () => [
      {
        field: "campaign_segment_names",
        headerName: "Campaign",
        flex: 4,
        valueGetter: (_value, row) => sortedByLocale(row.campaign_segment_names).join(", "),
        renderCell: ({row}) => (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 1}}>
            {row.campaign_segment_names.length === 0 ? (
              <Chip size_="small" variant_="inherit" label="Default" />
            ) : (
              row.campaign_segment_names.map((name, i) => (
                <Chip key={i} size_="small" variant_="inherit" label={name} />
              ))
            )}
          </Box>
        ),
      },
      {
        field: "account_count",
        headerName: "Accounts",
        flex: 1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
            <Text size={12} variant="inter600" sx={{color: Colors.grey._700}}>
              Accounts
            </Text>
            <AccountCountInfoTooltip summaryType="campaign" />
          </Box>
        ),
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._1000}}>
            {row.account_count.toLocaleString()}
          </Text>
        ),
      },
      {
        field: "total_balance",
        headerName: "Face value",
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._1000}}>
            {prettifyCurrencyRounded(row.total_balance)}
          </Text>
        ),
      },
      {
        field: "audience_count",
        headerName: "Audiences",
        flex: 1,
        disableColumnMenu: true,
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._1000}}>
            {row.audience_count.toLocaleString()}
          </Text>
        ),
      },
      {
        field: "num_of_templates_per_channel",
        headerName: "Templates",
        flex: 1,
        disableColumnMenu: true,
        valueGetter: (_value, row) => sumTemplatesPerChannel(row.num_of_templates_per_channel),
        renderCell: ({row}) => (
          <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
            <Text size={14} sx={{color: Colors.grey._1000}}>
              {sumTemplatesPerChannel(row.num_of_templates_per_channel).toLocaleString()}
            </Text>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setChannelsDrawerCampaign(row);
              }}
              sx={{color: Colors.grey._700}}
            >
              <ChevronRightIcon sx={{fontSize: 20}} />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title="Campaigns">
        <FiltersToggleButton panel={filterPanel} activeCount={countActiveFilters(filterValues)} />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by segment name"
          sx={{height: HEADER_CONTROL_HEIGHT}}
        />
      </ListPageHeader>
      <AudienceRecalculationBanner dataQueryKey={CAMPAIGNS_QUERY_KEY} />
      <Box sx={{display: "flex", flex: 1, minHeight: 0}}>
        <FilterSidebar filters={filters} values={filterValues} onChange={setFilterValues} panel={filterPanel} />
        <Box sx={{display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0}}>
          {error ? (
            <Text size={14}>Failed to load campaigns.</Text>
          ) : (
            <ListDataGrid
              rows={filteredRows}
              columns={columns}
              loading={isLoading}
              getRowId={(row) => row.campaign_segment_ids.join(",") || "default"}
              getRowUrl={getCampaignUrl}
              pageSizeOptions={[10, 25, 50, 100]}
              disableColumnMenu={false}
              columnHeaderHeight={LIST_HEADER_HEIGHT}
              noRowsText={
                debouncedSearch || hasActiveFilters(filterValues)
                  ? "No campaigns match your filters"
                  : "No campaigns yet"
              }
            />
          )}
        </Box>
      </Box>
      <TemplatesPerChannelDrawer
        audience={channelsDrawerCampaign}
        scope="campaign"
        onClose={() => setChannelsDrawerCampaign(null)}
      />
    </Box>
  );
};

export default CampaignsPage;
